import { useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { FileEdit, Loader2, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import i18n from '../../i18n'
import AiResultModal from '../ui/AiResultModal'
import type { AiDraftRequest, AiDraftResponse, AiSummaryResponse } from '@jr2os/shared'

const DRAFT_KINDS: Array<{ k: AiDraftRequest['kind']; labelKey: string }> = [
  { k: 'PROPUESTA', labelKey: 'aiPanel.draftKinds.proposal' },
  { k: 'SOW', labelKey: 'aiPanel.draftKinds.sow' },
  { k: 'NDA', labelKey: 'aiPanel.draftKinds.nda' },
  { k: 'INFORME', labelKey: 'aiPanel.draftKinds.report' },
  { k: 'ESPECIFICACION_TECNICA', labelKey: 'aiPanel.draftKinds.techSpec' },
  { k: 'CORREO_CLIENTE', labelKey: 'aiPanel.draftKinds.clientEmail' },
  { k: 'OTRO', labelKey: 'aiPanel.draftKinds.other' },
]

interface Props {
  opportunityId: string
  opportunityCode: string
}

export default function CaseAiPanel({ opportunityId, opportunityCode }: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [busy, setBusy] = useState<'none' | 'summary' | 'draft'>('none')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    title: string
    text: string
    conversationId: string | null
  } | null>(null)
  const [showDraft, setShowDraft] = useState(false)

  const onSummary = async () => {
    setBusy('summary')
    setError(null)
    try {
      const resp = await api.post<AiSummaryResponse>('/ai/summary', {
        contextType: 'OPPORTUNITY',
        contextId: opportunityId,
        style: 'BRIEF',
      })
      setResult({
        title: `${t('aiPanel.summaryTitle')} ${opportunityCode}`,
        text: resp.text,
        conversationId: resp.conversationId,
      })
    } catch (err) {
      setError(extractError(err))
    } finally {
      setBusy('none')
    }
  }

  return (
    <div className="surface p-5">
      <h3 className="t-primary font-semibold mb-3 flex items-center gap-2">
        <Sparkles size={14} className="text-jr2-400" />
        {t('aiPanel.title')}
      </h3>
      <div className="space-y-2">
        <button
          type="button"
          onClick={onSummary}
          disabled={busy !== 'none'}
          className="w-full text-left px-3 py-2 rounded-lg border border-base hover:bg-hover text-sm t-secondary disabled:opacity-50 flex items-center gap-2"
        >
          {busy === 'summary' ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Sparkles size={14} className="text-jr2-400" />
          )}
          {t('aiPanel.summarize')}
        </button>
        <button
          type="button"
          onClick={() => setShowDraft(true)}
          disabled={busy !== 'none'}
          className="w-full text-left px-3 py-2 rounded-lg border border-base hover:bg-hover text-sm t-secondary disabled:opacity-50 flex items-center gap-2"
        >
          <FileEdit size={14} className="text-jr2-400" />
          {t('aiPanel.draftCta')}
        </button>
      </div>
      {error && <p className="mt-3 text-xs text-rose-700 dark:text-rose-300">{error}</p>}

      {result && (
        <AiResultModal
          title={result.title}
          text={result.text}
          conversationId={result.conversationId}
          onClose={() => setResult(null)}
        />
      )}

      {showDraft && (
        <DraftModal
          opportunityId={opportunityId}
          onClose={() => setShowDraft(false)}
          onDone={(r) => {
            setShowDraft(false)
            setResult(r)
          }}
        />
      )}
    </div>
  )
}

interface DraftModalProps {
  opportunityId: string
  onClose: () => void
  onDone: (r: { title: string; text: string; conversationId: string }) => void
}

function DraftModal({ opportunityId, onClose, onDone }: DraftModalProps) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [kind, setKind] = useState<AiDraftRequest['kind']>('PROPUESTA')
  const [instructions, setInstructions] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (instructions.trim().length < 5) {
      setError(t('aiPanel.errors.instructionsTooShort'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const resp = await api.post<AiDraftResponse>('/ai/draft', {
        opportunityId,
        kind,
        instructions: instructions.trim(),
      })
      const kindLabel = (() => {
        const entry = DRAFT_KINDS.find((d) => d.k === kind)
        return entry ? t(entry.labelKey) : kind
      })()
      onDone({
        title: `${t('aiPanel.draftTitle')} ${kindLabel}`,
        text: resp.text,
        conversationId: resp.conversationId,
      })
    } catch (err) {
      setError(extractError(err))
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">{t('aiPanel.draftHeading')}</h3>
        <div>
          <label className="block text-xs t-muted mb-1">{t('aiPanel.fields.kind')}</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as AiDraftRequest['kind'])}
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40"
          >
            {DRAFT_KINDS.map((d) => (
              <option key={d.k} value={d.k}>
                {t(d.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('aiPanel.fields.instructions')}</label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={6}
            placeholder={t('aiPanel.placeholders.instructions')}
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40"
          />
          <p className="text-[10px] t-faint mt-1">
            {t('aiPanel.draftHint')}
          </p>
        </div>
        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t('aiPanel.generate')}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; code?: string } | undefined
    if (body?.code === 'AI_NOT_CONFIGURED') {
      return i18n.t('opportunities:aiPanel.errors.notConfigured')
    }
    return body?.error ?? err.message
  }
  return err instanceof Error ? err.message : i18n.t('opportunities:aiPanel.errors.unexpected')
}
